import React, { useEffect, useState } from 'react';
import { TemplateFormData, Category, Language } from '../../../../views/marketing-pages/templates/types';
import { RcsUseCase, RCS_USE_CASES } from './rcs-types';
import { SectionLabel } from '../utiz';
import Select from '../../../select';
import { RcsMessageBuilder } from './rcs-message-builder';
import { Info } from 'lucide-react';
import useRequest from '../../../hook/use-req';

interface RcsAgent {
  id: string;
  agent_id: string;
  agent_name: string;
}

interface RcsFormProps {
  formData: TemplateFormData;
  onChange: (updates: Partial<TemplateFormData>) => void;
  readOnly?: boolean;
}

export const RcsForm: React.FC<RcsFormProps> = ({ formData, onChange, readOnly }) => {
  const token = localStorage.getItem('authToken');
  const [agents, setAgents] = useState<RcsAgent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(true);

  const { makeRequest: fetchAgents } = useRequest('/rcs/agents/', 'GET', {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const loadAgents = async () => {
      setLoadingAgents(true);
      try {
        const [response] = await fetchAgents();
        const agentList = response?.data ?? response ?? [];
        setAgents(Array.isArray(agentList) ? agentList : []);
      } catch (error) {
        console.error('Failed to fetch RCS agents:', error);
      } finally {
        setLoadingAgents(false);
      }
    };
    loadAgents();
  }, []);

  const handleCategoryChange = (value: string) => {
    onChange({ category: value as Category });
  };

  const handleUseCaseChange = (value: string) => {
    onChange({ rcsUseCase: value as RcsUseCase });
  };

  const handleLanguageChange = (value: string) => {
    onChange({ language: value as Language });
  };

  const handleAgentChange = (value: string) => {
    onChange({ rcsAgentId: value });
  };

  const agentOptions = agents.map((agent) => ({
    label: agent.agent_name || agent.agent_id,
    value: agent.agent_id,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-800 rounded-lg">
        <Info size={16} className="text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-700 dark:text-blue-400">
          RCS templates require brand & agent approval from Google.{' '}
          <a href="https://developers.google.com/business-communications/rcs-business-messaging" target="_blank" rel="noopener noreferrer" className="underline font-medium">
            Learn more
          </a>
        </p>
      </div>

      <div>
        <SectionLabel label="RCS Agent" />
        {loadingAgents ? (
          <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
            <span className="text-sm text-gray-500 dark:text-gray-400">Loading agents...</span>
          </div>
        ) : agentOptions.length > 0 ? (
          readOnly ? (
            <div className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {agents.find(a => a.agent_id === formData.rcsAgentId)?.agent_name || formData.rcsAgentId || 'No agent selected'}
              </span>
            </div>
          ) : (
            <Select
              value={formData.rcsAgentId || ''}
              onChange={handleAgentChange}
              options={agentOptions}
              placeholder="Select an agent"
            />
          )
        ) : (
          <div className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800">
            <span className="text-sm text-gray-500 dark:text-gray-400">No RCS agents available</span>
          </div>
        )}
        <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">
          Select your registered RCS agent
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <SectionLabel label="Category" />
          <Select
            value={formData.category}
            onChange={handleCategoryChange}
            options={[
              { label: 'Marketing', value: 'Marketing' },
              { label: 'Authentication', value: 'Authentication' },
              { label: 'Utility', value: 'Utility' },
            ]}
          />
        </div>
        <div>
          <SectionLabel label="Use Case" />
          <Select
            value={formData.rcsUseCase || 'Order Update'}
            onChange={handleUseCaseChange}
            options={RCS_USE_CASES}
          />
        </div>
        <div>
          <SectionLabel label="Language" />
          <Select
            value={formData.language}
            onChange={handleLanguageChange}
            options={[
              { label: 'English (US)', value: 'English (US)' },
              { label: 'English (UK)', value: 'English (UK)' },
              { label: 'Spanish', value: 'Spanish' },
              { label: 'French', value: 'French' },
            ]}
          />
        </div>
      </div>

      <hr className="border-gray-100 dark:border-gray-800" />

      <RcsMessageBuilder
        components={formData.rcsComponents || []}
        onChange={(components) => onChange({ rcsComponents: components })}
        readOnly={readOnly}
      />

    </div>
  );
};
